import { callApi } from './api.service';

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

export interface TokenResponse {
  token: string;
  webrtc_url: string;
}

export async function fetchToken(
  jobDescription: string,
  accessCode: string,
  language: string = 'en',
): Promise<TokenResponse> {
  const response = await callApi<TokenResponse>(`${apiServerUrl}/api/sales-demo/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ job_description: jobDescription, access_code: accessCode, language }),
  });
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export interface TranscriptTurn {
  role: 'user' | 'assistant';
  text: string;
}

export interface DimensionResult {
  name: string;
  score: number;
  evidence: string;
  feedback: string;
}

export interface SalesEvaluation {
  dimensions: DimensionResult[];
  summary: string;
  strengths: string[];
  improvements: string[];
}

export interface EvaluateResponse {
  evaluation: SalesEvaluation;
}

export async function evaluateSession(
  transcript: TranscriptTurn[],
  jobDescription: string,
  accessCode: string,
): Promise<EvaluateResponse> {
  const response = await callApi<EvaluateResponse>(`${apiServerUrl}/api/sales-demo/evaluate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      transcript: transcript.map(t => ({ role: t.role, text: t.text })),
      job_description: jobDescription,
      access_code: accessCode,
    }),
  });
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export async function evaluateWithAudio(
  audioBlob: Blob,
  assistantTranscript: TranscriptTurn[],
  jobDescription: string,
  accessCode: string,
): Promise<EvaluateResponse> {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'recording.webm');
  formData.append(
    'assistant_transcript',
    JSON.stringify(assistantTranscript.map(t => ({ role: t.role, text: t.text }))),
  );
  formData.append('job_description', jobDescription);
  formData.append('access_code', accessCode);

  const response = await callApi<EvaluateResponse>(`${apiServerUrl}/api/sales-demo/evaluate-with-audio`, {
    method: 'POST',
    body: formData,
  });
  if (response.error) throw new Error(response.error);
  return response.data!;
}

export async function extractTextFromFile(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await callApi<{ text: string }>(`${apiServerUrl}/api/sales-demo/extract-text`, {
    method: 'POST',
    body: formData,
  });
  if (response.error) throw new Error(response.error);
  return response.data!.text;
}
